import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import { effectiveContent } from '../../../lib/file-record';
import { DocTooLargeError, inlineDocTooLarge, useDocText } from '../../../lib/doc-content';
import { docSource, type DocViewerKind } from '../../../lib/doc-viewer';
import { editorRefusal, editorSeedBody } from '../../../lib/doc-editable';
import { draftBlocker, draftFileName, draftPath } from '../../../lib/file-draft';
import { DocDraftFields } from './DocDraftFields';
import { useFileWrite } from '../../../lib/use-file-write';
import { useFileMove } from '../../../lib/use-file-move';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { filePath, folderOf, nameOf, writeDirectoryOf, writeKeyOf, type WriteDirectory } from '../../../lib/file-browser';
import { fileEditableBody, MAX_WRITABLE_CONTENT_CHARS } from '../../../lib/file-edit';
import { Button, ScrollArea } from '../ui';
import { Markdown } from '../../Markdown';
import { DocErrorState, DocNoticeState, DocShimmer } from './DocViewerStates';
import styles from './DocEditView.module.css';

// Editing a text file in place — the pencil's destination, inside the same viewer the file was being read
// in. `.md`, `.txt`, `.csv` and their siblings; `isEditableFileName` (common-lib) is the one rule that says
// which, shared with the two native apps and with the endpoint that accepts the save.
//
// It loads its own body rather than taking one from `DocViewerBody`, through the identical
// `docSource` → `useDocText` pair, so the editor and the reader can never be looking at different bytes.
//
// The markdown mode is source-plus-preview rather than WYSIWYG because the feature has to mean the same
// thing on all three surfaces: a rich-text markdown editor is a document model (ProseMirror/TipTap) with no
// equivalent on iOS or Android, which render markdown with hand-rolled `AttributedString`/`AnnotatedString`
// renderers. Source-with-preview reuses the renderer each platform already ships.
export function DocEditView({
  file,
  kind,
  directory,
  onBi,
  onClose,
  workspaceNames = {},
  onPickFolder,
  draft,
}: {
  file: WorkspaceFile;
  /** Resolved by the caller, exactly as `DocViewerBody` takes it — never re-derived here. Decides whether
   *  the Preview tab renders markdown or is hidden (nothing to preview for plain text and CSV). */
  kind: DocViewerKind | null;
  /** The file-system directory to save into — `workspaces` (default) or `brain`. */
  directory?: WriteDirectory;
  onBi: (action: string) => void;
  /** Leave the editor and go back to reading. Called on Cancel and after a successful Save. */
  onClose: () => void;
  /** Space id → display name, so the destination row never shows a raw id. */
  workspaceNames?: Record<string, string>;
  /** Opens the destination picker; resolves to the chosen folder, or `null` if dismissed. A surface with
   *  nowhere to host a second modal omits it, and the destination is then shown but not changeable. */
  onPickFolder?: () => Promise<string | null>;
  /** Present when this is a NEW file: Save CREATES rather than overwriting, and the fields start empty.
   *  Editing an existing file shows the same three fields, seeded from the file. See `lib/file-draft.ts`. */
  draft?: { startFolder: string; onCreated?: (path: string) => void };
}) {
  const { t } = useTranslation();
  const { state, error, write } = useFileWrite();
  const { move } = useFileMove();

  // The three fields, for a NEW file and for an existing one alike: editing a file is the same act as
  // creating it, minus the part where it does not exist yet. Renaming from here is a MOVE (the path is the
  // file's identity), which is why Save branches below.
  const [draftName, setDraftName] = useState(draft ? '' : nameOf(file.name));
  const [draftDescription, setDraftDescription] = useState(draft ? '' : (file.hint ?? ''));
  const [folder, setFolder] = useState(draft ? draft.startFolder : folderOf(filePath(file)));

  const source = docSource(effectiveContent(file));
  const remoteUrl = source?.kind === 'remote' ? source.url : null;
  const query = useDocText(remoteUrl);
  const loaded = source?.kind === 'inline' ? source.text : query.data ?? null;

  const tooLargeToRead = query.error instanceof DocTooLargeError || (source?.kind === 'inline' && inlineDocTooLarge(source.text));
  // `lib/doc-editable.ts`, shared with the two native editors. `editorSeedBody` is what says an EMPTY file
  // opens: `docSource` answers "is there a document to READ", which for an empty body is no, and reading
  // that as "not editable" made the Files `+` refuse the file it had just written. It answers `null` while
  // a REMOTE body is still in flight, which keeps the draft unseeded until the real text lands.
  const refusal = editorRefusal(file, !!tooLargeToRead);
  const editableBody = editorSeedBody(loaded, !!remoteUrl);

  // An artifact old enough to predate the pointer-fields migration still opens with a `<!--sym-file:…-->`
  // metadata header. It is not the document, so it is not what gets edited — and the endpoint puts it back
  // on save from the file it already has. A no-op for anything written since.
  const original = useMemo(() => (editableBody == null ? null : fileEditableBody(editableBody)), [editableBody]);
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  // Seed the body the first time one arrives, and never again: re-seeding on every render of a changed
  // `original` would discard what the user has typed the moment a background refetch resolved.
  useEffect(() => {
    setBodyDraft((current) => (current === null && original !== null ? original : current));
  }, [original]);

  // `!!remoteUrl &&` IS THE WHOLE GUARD, and dropping it is not a subtle bug — it is a permanent shimmer.
  // React Query v4 reports a DISABLED query as `status: 'loading'`, and `useDocText(null)` is disabled
  // (`enabled: !!url`), so a file whose body is stored INLINE — the common case — would sit on the loading
  // branch forever with nothing in flight. `DocViewerBody` carries the same guard for the same reason.
  const loading = !!remoteUrl && query.isLoading;
  const failed = !!remoteUrl && query.isError;

  const body = bodyDraft ?? '';
  const overLimit = body.length > MAX_WRITABLE_CONTENT_CHARS;
  // A DRAFT saves on being complete, not on being changed: a new file with a name and a destination is
  // saveable with nothing typed in it, because an empty file is a real file. An existing file saves only
  // when its body actually moved.
  const blocker = draftBlocker({ name: draftName, folder });
  const bodyDirty = original !== null && body !== original;
  // Editing covers the fields too now, so Save must notice a rename or a new description even when the
  // body was not touched — otherwise the only way to rename is to type a character and delete it.
  const fieldsDirty =
    !draft &&
    (draftPath({ name: draftName, folder }) !== filePath(file) || draftDescription.trim() !== (file.hint ?? '').trim());
  const canSave = draft ? blocker === null : blocker === null && (bodyDirty || fieldsDirty);

  const save = async () => {
    if (draft) {
      // `create` is what makes this a CREATE rather than a write: the endpoint answers `file_exists`
      // instead of overwriting, and the file-system keeps no revisions to undo that with.
      onBi('draft_save');
      const path = draftPath({ name: draftName, folder });
      // The DESTINATION decides the directory — the picker can walk from a Space into an agent's folder
      // without leaving this editor, so reading it off the folder is the only answer that stays right.
      // `writeKeyOf` because the tree's path and the directory's key are not the same string for an
      // agent's folder — see its doc, and the `brain/brain/` it exists to prevent.
      const ok = await write(writeKeyOf(path), body, {
        create: true,
        directory: writeDirectoryOf(folder) ?? undefined,
        hint: draftDescription.trim(),
      });
      if (ok) {
        draft.onCreated?.(path);
        onClose();
      }
      return;
    }
    // An existing file. The name and the destination are editable here too, and changing either changes the
    // file's PATH — which is a move, not a write, because the path is the file's identity and the old key
    // has to stop existing. `move` carries the edited body and description with it so the rename and the
    // edit are one act rather than two that can half-apply.
    const from = filePath(file);
    const to = draftPath({ name: draftName, folder });
    if (to && to !== from) {
      onBi('edit_rename');
      if (await move(file, folder, { name: draftFileName(draftName), content: body, hint: draftDescription.trim() })) {
        onClose();
      }
      return;
    }
    onBi('edit_save');
    if (await write(writeKeyOf(from), body, { directory: writeDirectoryOf(from) ?? directory, hint: draftDescription.trim() })) onClose();
  };

  // A file whose bytes live on WixMP has no inline body to replace — saving would mean re-uploading. The
  // endpoint refuses it too; refusing here as well means the user is told before typing rather than after.
  if (refusal) {
    return <DocNoticeState message={t('docEditor.notEditable')} />;
  }
  if (loading) return <DocShimmer />;
  if (failed || original === null) {
    return (
      <DocErrorState
        onRetry={() => {
          onBi('edit_retry');
          void query.refetch();
        }}
      />
    );
  }

  const previewable = kind === 'markdown';
  const dir = messageDir(body);

  return (
    <div className={styles.editor}>
      {(
        <DocDraftFields
          name={draftName}
          onName={setDraftName}
          description={draftDescription}
          onDescription={setDraftDescription}
          folder={folder}
          workspaceNames={workspaceNames}
          onPickFolder={
            onPickFolder &&
            (() => {
              void onPickFolder().then((picked) => {
                if (picked !== null) setFolder(picked);
              });
            })
          }
          disabled={state === 'saving'}
          creating={!!draft}
        />
      )}
      <div className={styles.toolbar}>
        {previewable ? (
          <div className={styles.tabs} role="group" aria-label={t('docEditor.modeLabel')}>
            <Button variant={mode === 'edit' ? 'contained' : 'outlined'} size="tiny" onClick={() => setMode('edit')}>
              {t('docEditor.edit')}
            </Button>
            <Button
              variant={mode === 'preview' ? 'contained' : 'outlined'}
              size="tiny"
              onClick={() => {
                onBi('edit_preview');
                setMode('preview');
              }}
            >
              {t('docEditor.preview')}
            </Button>
          </div>
        ) : (
          <span />
        )}
        <div className={styles.tabs}>
          <Button variant="outlined" size="tiny" onClick={onClose} disabled={state === 'saving'}>
            {t('docEditor.cancel')}
          </Button>
          <Button size="tiny" onClick={() => void save()} disabled={!canSave || overLimit || state === 'saving'}>
            {state === 'saving' ? t('docEditor.saving') : draft ? t('files.new.confirm') : t('docEditor.save')}
          </Button>
        </div>
      </div>

      {/* One line, and it only appears when there is something to say. `overLimit` is the one the user can
          act on while typing, so it wins over a stale save error. */}
      {overLimit ? (
        <p className={styles.notice} role="status">
          {t('docEditor.tooLong', { max: MAX_WRITABLE_CONTENT_CHARS })}
        </p>
      ) : error ? (
        <p className={styles.error} role="alert">
          {t(`docEditor.error.${error}`)}
        </p>
      ) : null}

      {mode === 'preview' ? (
        <ScrollArea className={styles.preview}>
          <div dir={dir}>
            {/* `variant="chat"` is what enables remark-gfm — the same requirement `MarkdownDocView`
                documents, and the preview must render exactly what the reader will see. */}
            <Markdown variant="chat" dir={dir}>
              {body}
            </Markdown>
          </div>
        </ScrollArea>
      ) : (
        <textarea
          className={styles.textarea}
          value={body}
          dir={dir}
          spellCheck={false}
          onChange={(event) => setBodyDraft(event.target.value)}
          aria-label={t('docEditor.textareaLabel', { file: draft ? draftFileName(draftName) || t('files.new.title') : file.name })}
        />
      )}
    </div>
  );
}
